// Menu.jsx — FINAL (All categories + all items + PDF descriptions)

import React from "react";
import templeOverlay from "../assets/temple-overlay.png";
import MobileNavbar from "../components/MobileNavbar";
import { Link } from "react-router-dom";


/* ================= THEME (UNCHANGED) ================= */
const theme = {
  bg: "#FFF9F0",
  card: "#FFFFFF",
  ink: "#12262A",
  sub: "#3C4A4E",
  divider: "#E8DDC8",
  accent: "#C69A35",
  wash: "#F6EAD0",
};

const fonts = {
  heading:
    '"Playfair Display", ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
  body:
    'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial',
};

/* ================= UI COMPONENTS (UNCHANGED) ================= */
const Money = ({ value }) => (
  <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
    ${Number(value).toFixed(2)}
  </span>
);

const Item = ({ name, description, price }) => (
  <li
    style={{
      background: theme.card,
      border: `1px solid ${theme.divider}`,
      borderRadius: 14,
      padding: 14,
      boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <h4
        style={{
          margin: 0,
          fontFamily: fonts.body,
          fontSize: 18,
          fontWeight: 800,
          color: theme.ink,
        }}
      >
        {name}
      </h4>
      {price != null && (
        <div style={{ color: theme.ink }}>
          <Money value={price} />
        </div>
      )}
    </div>

    {description && (
      <p
        style={{
          marginTop: 6,
          marginBottom: 0,
          fontFamily: fonts.body,
          color: theme.sub,
          lineHeight: 1.55,
        }}
      >
        {description}
      </p>
    )}
  </li>
);

const Section = ({ title, items }) => (
  <section style={{ marginBottom: 32 }}>
    <h3
      style={{
        fontFamily: fonts.heading,
        fontSize: 26,
        fontWeight: 800,
        color: theme.ink,
        borderBottom: `2px solid ${theme.accent}`,
        display: "inline-block",
        paddingBottom: 4,
        marginBottom: 16,
        textTransform: "uppercase",
      }}
    >
      {title}
    </h3>

    <ul
      style={{
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "grid",
        gap: 14,
      }}
    >
      {items.map((item, i) => (
        <Item key={i} {...item} />
      ))}
    </ul>
  </section>
);

/* ================= MENU DATA (FROM PDF) =================
   Notes:
   - Descriptions included wherever PDF provides them.
   - Items without descriptions in the PDF are left without description.
*/
const menuData = [
  {
    title: "Soup",
    items: [
      {
        name: "Kollu Rasam",
        price: 7.99,
        description: "Horse gram-based rasam with earthy flavors and spices.",
      },
      {
        name: "Milagu Rasam",
        price: 7.99,
        description: "Peppery tamarind soup with a spicy kick.",
      },
    ],
  },

  {
    title: "Appetizers",
    items: [
      {
        name: "Onion Pakoda",
        price: 8.99,
        description: "Crispy fritters of onion strands mixed with chickpea flour and spices.",
      },
      {
        name: "Paneer 65 Dry",
        price: 14.99,
        description: "Spicy and crispy fried paneer cubes tempered with curry leaves.",
      },
      {
        name: "Gobi 65 Dry",
        price: 12.99,
        description: "Golden-fried cauliflower coated in bold South Indian spices, fresh curry leaves, and chilies.",
      },
      {
        name: "Masala Papad",
        price: 6.99,
        description: "Crispy roasted papad with fresh onions, tomatoes, and spices.",
      },
      {
        name: "Aloo Bonda",
        price: 6.99,
        description: "Deep-fried spiced potato dumplings in chickpea batter.",
      },
      {
        name: "Mysore Bonda",
        price: 10.99,
        description: "Crispy, pillowy-soft fritters made with all-purpose flour, cumin, and ginger.",
      },
      {
        name: "Chilli Paneer Dry",
        price: 14.99,
        description: "Crispy paneer cubes tossed in spicy chilli sauce with curry leaves.",
      },
      {
        name: "Chilli Idli",
        price: 11.99,
        description: "Crispy fried idli tossed in a spicy tangy sauce with onions and bell peppers.",
      },
      {
        name: "Podi Idli",
        price: 10.99,
        description: "Soft idlies tossed in spiced podi powder with a hint of ghee.",
      },
      {
        name: "Chilli Bajji",
        price: 8.99,
        description: "Whole green chillies dipped in batter and fried to a perfect crunch.",
      },
    ],
  },

  {
    title: "Idli/Vada",
    items: [
      {
        name: "2 Vada and 1 Idli",
        price: 10.49,
        description: "Perfect combination of soft idlies and crispy vadas served with sambar and chutney.",
      },
      {
        name: "2 Idli and 1 Vada",
        price: 10.49,
        description: "Perfect combination of soft idlies and crispy vadas served with sambar and chutney.",
      },
      {
        name: "Medhu Vada",
        price: 9.99,
        description: "Crispy lentil doughnut, deep-fried and served with chutneys and sambar.",
      },
      {
        name: "Rasam Vada (Dipped)",
        price: 11.49,
        description: "Lentil dumplings dipped in rasam.",
      },
      {
        name: "Sambar Vada (Dipped)",
        price: 9.99,
        description: "Lentil dumplings dipped in sambar.",
      },
      {
        name: "Dahi Vada (Dipped)",
        price: 11.99,
        description: "Lentil dumplings soaked in chilled yogurt garnished with spices.",
      },
      {
        name: "Idli",
        price: 9.99,
        description: "Steamed savory rice cake served with chutneys and sambar.",
      },
      {
        name: "Thatteidli",
        price: 10.99,
        description: "Steamed savory bigger size rice cake comes with ghee and podi on top.",
      },
      {
        name: "Mini Idli",
        price: 10.99,
        description: "Mini idlies served with chutneys and sambar.",
      },
      {
        name: "Mini Idli (Dipped)",
        price: 11.99,
        description: "Mini idlies dipped in sambar.",
      },
      {
        name: "Rasam Idli (Dipped)",
        price: 11.49,
        description: "Steamed savory rice cake dipped in rasam.",
      },
      {
        name: "Sambar Idli (Dipped)",
        price: 9.99,
        description: "Steamed savory rice cake dipped in sambar.",
      },
    ],
  },

  {
    title: "Uttapams",
    items: [
      {
        name: "Plain Uttapam",
        price: 11.99,
        description: "Traditional South Indian thick rice-lentil pancake.",
      },
      {
        name: "5 Cocktail Uttapam",
        price: 16.99,
        description: "Five small different uttapams of chef's choice.",
      },
      {
        name: "Paneer Uttapam",
        price: 15.99,
        description: "Uttapam topped with marinated paneer and garnished with cilantro.",
      },
      {
        name: "Chilli Cheese Uttapam",
        price: 15.99,
        description: "Uttapam topped with green chillies and mozzarella cheese.",
      },
      {
        name: "Podi Masala Uttapam",
        price: 14.99,
        description: "Uttapam topped with vegetables and podi powder.",
      },
      {
        name: "Podi Uttapam",
        price: 13.99,
        description: "Uttapam topped with spicy podi powder.",
      },
      {
        name: "Tomato Onion Uttapam",
        price: 13.99,
        description: "Uttapam topped with tomato and onion.",
      },
      {
        name: "Onion Chilli Uttapam",
        price: 13.99,
        description: "Uttapam topped with onion and green chillies.",
      },
      {
        name: "Onion Uttapam",
        price: 12.99,
        description: "Uttapam topped with onion.",
      },
      {
        name: "Pizza Uttapam",
        price: 15.99,
        description: "Uttapam topped with tomato sauce, cheese, onion, and bell pepper.",
      },
      {
        name: "Tomato Onion Chilli Uttapam",
        price: 14.99,
        description: "Uttapam topped with tomato, onion, and green chillies.",
      },
    ],
  },

  {
    title: "Dosa",
    items: [
      {
        name: "Mysore Dosa",
        price: 14.99,
        description: "Crispy dosa with a spread of mysore paste.",
      },
      {
        name: "Mysore Masala Dosa",
        price: 15.99,
        description: "Crispy dosa with a spread of mysore potato masala.",
      },
      {
        name: "Masala Dosa",
        price: 14.99,
        description: "Crispy dosa filled with potato masala.",
      },
      {
        name: "Paper Masala Dosa",
        price: 12.99,
        description: "Paper thin rice crepes filled with potato masala.",
      },
      {
        name: "Ghee Podi Masala Dosa",
        price: 15.99,
        description: "Dosa spiced with podi and filled with potato masala.",
      },
      {
        name: "Ghee Podi Dosa",
        price: 14.99,
        description: "Dosa spiced with podi.",
      },
      {
        name: "Carnatic Special Dosa - Cheese (+$2) / Paneer (+$2)",
        price: 17.99,
        description: "Crisp dosa filled with either melted cheese or fresh crumbled paneer and Chef special masala.",
      },
      {
        name: "Ghee Roast Dosa",
        price: 15.99,
        description: "Crispy dosa roasted with aromatic ghee.",
      },
      {
        name: "Ghee Roast Masala Dosa",
        price: 16.99,
        description: "Crispy golden dosa roasted with aromatic ghee and potato masala.",
      },
      {
        name: "Roasted Garlic Masala Dosa",
        price: 16.99,
        description: "Dosa with spread of roasted garlic masala paste.",
      },
      {
        name: "Andhra Kara Dosa",
        price: 15.99,
        description: "Crispy dosa with a kick of Andhra spices.",
      },
      {
        name: "Andhra Kara Masala Dosa",
        price: 16.99,
        description: "Crispy dosa with a kick of Andhra spices and potato masala.",
      },
      {
        name: "Family Crisp Dosa",
        price: 21.99,
        description: "Extra-large crispy paper dosa served with coconut chutney, garlic chutney and sambar.",
      },
      {
        name: "Set Masala Dosa",
        price: 15.99,
        description: "Set of 2 soft and spongy dosas served with chutney and sambar and potato masala.",
      },
      {
        name: "Set Dosa",
        price: 14.99,
        description: "Set of 2 soft and spongy dosas served with chutney and sambar.",
      },
      {
        name: "Schezwan Masala Dosa",
        price: 16.99,
        description: "Crispy masala dosa with a twist of schezwan with potato masala.",
      },
      {
        name: "Schezwan Dosa",
        price: 15.99,
        description: "Crispy masala dosa with a twist of schezwan.",
      },
      {
        name: "Spinach Masala Dosa",
        price: 16.99,
        description: "Spinach dosa stuffed with potato masala.",
      },
      {
        name: "Spinach Dosa",
        price: 15.99,
        description: "Crispy dosa with a spread of spinach chutney.",
      },
      {
        name: "Cheese Masala Dosa",
        price: 16.99,
        description: "Crispy dosa with mozzarella cheese and potato masala.",
      },
      {
        name: "Cheese Dosa",
        price: 15.99,
        description: "Crispy dosa with mozzarella cheese.",
      },
      {
        name: "Paneer Bhurji Dosa",
        price: 15.99,
        description: "Paneer bhurji filling is wrapped inside a crispy dosa.",
      },
      {
        name: "Chilli Paneer Dosa",
        price: 15.99,
        description: "Crispy dosa stuffed with paneer, chilli, and house spices.",
      },
      {
        name: "Cheese Chilli Dosa",
        price: 17.99,
        description: "Crispy dosa topped with melted cheese and green chillies.",
      },
      {
        name: "Neer Dosa with Kurma",
        price: 16.99,
        description: "Soft, thin, lacy crepe served with vegetable kurma.",
      },
      {
        name: "Neer Dosa with Chutney",
        price: 13.99,
        description: "Soft, thin, lacy crepe served with chutney.",
      },
    ],
  },

  {
    title: "Benne Dosa Combo",
    items: [
      {
        name: "Benne Plain Dosa",
        price: 15.99,
        description: "Tea/Coffee (+$2). Sweet of the Day (+$2). Bengaluru style butter dosa served with chutney and sambar.",
      },
      {
        name: "Benne Podi Masala Dosa",
        price: 17.99,
        description: "Tea/Coffee (+$2). Sweet of the Day (+$2). Bengaluru style butter masala dosa topped with potato masala and spicy podi powder.",
      },
      {
        name: "Benne Podi Plain Dosa",
        price: 16.99,
        description: "Tea/Coffee (+$2). Sweet of the Day (+$2). Bengaluru style butter dosa topped with spicy podi powder.",
      },
      {
        name: "Benne Masala Dosa",
        price: 16.99,
        description: "Tea/Coffee (+$2). Sweet of the Day (+$2). Bengaluru style butter dosa filled with potato masala.",
      },
      {
        name: "Benne Andhra Kara Masala Dosa",
        price: 17.99,
        description: "Tea/Coffee (+$2). Sweet of the Day (+$2). Bengaluru style butter masala dosa with spicy Andhra chilli paste and potato masala.",
      },
      {
        name: "Benne Andhra Kara Plain Dosa",
        price: 16.99,
        description: "Tea/Coffee (+$2). Sweet of the Day (+$2). Bengaluru style butter dosa with spicy Andhra chilli paste.",
      },
    ],
  },

  {
    title: "Rava Dosa",
    items: [
      {
        name: "Onion Rava Dosa",
        price: 14.99,
        description: "Crispy semolina crepe with onion.",
      },
      {
        name: "Rava Masala Dosa",
        price: 14.99,
        description: "Rava dosa stuffed with potato masala.",
      },
      {
        name: "Rava Onion Chilli Dosa",
        price: 15.99,
        description: "Crispy semolina crepe with onion and chilli.",
      },
      {
        name: "Plain Rava Dosa",
        price: 13.99,
        description: "Crispy semolina crepe.",
      },
      {
        name: "Onion Rava Masala Dosa",
        price: 15.99,
        description: "Crispy semolina crepe with onion and potato masala.",
      },
    ],
  },

  {
    title: "Kids Zone",
    items: [
      {
        name: "Nutella Dosa",
        price: 6.99,
        description: "Crispy dosa topped with delicious Nutella spread.",
      },
      {
        name: "Kids Meal",
        price: 12.99,
        description: "Mini Dosa (Plain or Cheese or Chocolate), French Fries or Vada, Mini Idlis (5 pcs), Chutney, Sambar, Rava Kesari or Ice Cream.",
      },
      {
        name: "Kids Cone Dosa",
        price: 7.99,
        description: "Crispy dosa shaped like a cone.",
      },
      {
        name: "French Fries",
        price: 5.99,
        description: "Crispy golden potato fries.",
      },
      {
        name: "Kids Cheese Dosa",
        price: 6.99,
        description: "Mini dosa topped with melted cheese, perfect for kids.",
      },
    ],
  },

  {
    title: "Combo Special",
    items: [
      {
        name: "Mini Tiffin",
        price: 15.99,
        description: "1 idli, 1 vada, 1 mini dosa, sweet, and upma served with coconut chutney and sambar.",
      },
      {
        name: "Executive Thali",
        price: 14.99,
        description: "Plain rice, curry, fry, dal or sambar, curd, pickle, papad (extra rice $1.99).",
      },
      {
        name: "South Indian Thali",
        price: 20.99,
        description: "Plain rice, 2 puri or 1 chapati, 2 curries, fry, dal, sambar, rasam, curd, 1 vada, sweet, pickle, papad (extra rice $1.99).",
      },
      {
        name: "South Indian Thali Unlimited (Dine-In Only, No Sharing, No Takeout)",
        price: 23.99,
        description: "Unlimited plain rice, puri or chapati, 2 curries, fry, dal, sambar, rasam, curd, vada, sweet, pickle, papad.",
      },
    ],
  },

  {
    title: "Tiffins",
    items: [
      {
        name: "Rava Upma",
        price: 10.99,
        description: "Semolina porridge cooked with vegetables and garnished with nuts.",
      },
      {
        name: "Puri with Aloo Bhajji (3 puri)",
        price: 13.99,
        description: "Deep fried puffed bread served with potato kurma.",
      },
      {
        name: "Puri with Veg Kurma (3 puri)",
        price: 15.99,
        description: "Deep fried puffed bread served with veg kurma.",
      },
      {
        name: "Pongal Tiffin",
        price: 13.49,
        description: "Moong dal and rice simmered with ghee, black pepper, cumin, and cashews, served with chutney and sambar.",
      },
    ],
  },

  {
    title: "Curries",
    items: [
      {
        name: "Ennai Kathrikai/Eggplant Curry",
        price: 14.99,
        description: "Baby eggplant cooked with peanut, sesame, and tamarind.",
      },
      {
        name: "Veg Kurma Curry",
        price: 15.99,
        description: "Mixed veggies in a spiced coconut gravy.",
      },
      {
        name: "Kalan Melagu Masala",
        price: 15.99,
        description: "Mushroom curry cooked with black pepper and spices.",
      },
      {
        name: "Kadala Gassi",
        price: 15.99,
        description: "Black chickpeas cooked in a roasted coconut gravy.",
      },
    ],
  },

  {
    title: "Rice",
    items: [
      {
        name: "Bisi Bele Bath",
        price: 13.99,
        description: "Spicy rice and lentil dish cooked with vegetables and tamarind.",
      },
      {
        name: "Tamarind Rice",
        price: 13.99,
        description: "Steamed rice mixed with tangy tamarind, roasted peanuts, curry leaves, and South Indian spices.",
      },
      {
        name: "Tomato Rice",
        price: 13.99,
        description: "Spiced rice cooked with tangy tomatoes and herbs.",
      },
      {
        name: "Lemon Rice",
        price: 13.99,
        description: "Tangy rice flavored with lemon juice, peanuts, and curry leaves.",
      },
      {
        name: "Sambar Rice",
        price: 11.99,
        description: "Rice mixed with flavorful lentil and vegetable sambar.",
      },
      {
        name: "Rasam Rice",
        price: 10.99,
        description: "Rice served with tangy and spicy rasam soup.",
      },
      {
        name: "Curd Rice",
        price: 8.99,
        description: "Soft rice mixed with yogurt and tempered with spices.",
      },
    ],
  },

  {
    title: "Biryani",
    items: [
      {
        name: "Chennai Style Paneer Biryani",
        price: 14.99,
        description: "Fragrant Basmati rice cooked with marinated paneer and the bold and authentic flavors.",
      },
      {
        name: "Mixed Veg Biryani",
        price: 13.99,
        description: "Fragrant Basmati rice cooked with a medley of garden-fresh vegetables and aromatic heritage spices.",
      },
    ],
  },

  {
    title: "Desserts",
    items: [
      {
        name: "Gulab Jamun",
        price: 4.99,
        description: "Soft, deep-fried milk dumplings soaked in warm sugar syrup.",
      },
      {
        name: "Gulab Jamun with Vanilla Ice Cream",
        price: 6.99,
        description: "Warm Gulab Jamun served with a scoop of vanilla ice cream.",
      },
      {
        name: "Sweet of the Day (Chef's Special)",
        price: 7.99,
        description: "Chef's special sweet of the day.",
      },
    ],
  },

  {
    title: "Ice Creams",
    items: [
      {
        name: "Chocolate",
        price: 4.99,
        description: "Scoop of creamy chocolate ice cream.",
      },
      {
        name: "Butterscotch",
        price: 4.99,
        description: "Scoop of creamy butterscotch ice cream.",
      },
      {
        name: "Strawberry",
        price: 4.99,
        description: "Scoop of creamy strawberry ice cream.",
      },
      {
        name: "Vanilla",
        price: 4.99,
        description: "Scoop of creamy vanilla ice cream.",
      },
      {
        name: "Mango",
        price: 4.99,
        description: "Scoop of creamy mango ice cream.",
      },
    ],
  },

  {
    title: "Falooda",
    items: [
      {
        name: "Royal Falooda",
        price: 10.99,
        description: "Rich dessert drink with vermicelli, basil seeds, jelly, and ice cream.",
      },
      {
        name: "Carnatic Cafe Special Falooda",
        price: 10.99,
        description: "Rich dessert drink with vermicelli, extra basil seeds, jelly, nuts and ice cream.",
      },
      {
        name: "Mango Falooda",
        price: 8.99,
        description: "Rich dessert drink with vermicelli, luscious mango pulp, basil seeds, jelly, and ice cream.",
      },
    ],
  },

  {
    title: "Soft Drinks",
    items: [
      { name: "Ginger Ale", price: 2.49, description: "Chilled carbonated soft drink." },
      { name: "Canada Dry", price: 2.49, description: "Chilled carbonated soft drink." },
      { name: "Chili Soda", price: 2.49, description: "Chilled carbonated soft drink." },
      { name: "Orange Crush", price: 2.49, description: "Chilled carbonated soft drink." },
      { name: "Fanta", price: 2.49, description: "Chilled carbonated soft drink." },
      { name: "Diet Coke", price: 2.49, description: "Chilled carbonated soft drink." },
      { name: "Coke", price: 2.49, description: "Chilled carbonated soft drink." },
      { name: "Coke Zero", price: 2.49, description: "Chilled carbonated soft drink." },
      { name: "Sprite", price: 2.49, description: "Chilled carbonated soft drink." },
      { name: "7Up", price: 2.49, description: "Chilled carbonated soft drink." },
    ],
  },

  {
    title: "Beverages",
    items: [
      {
        name: "Madras Filter Coffee",
        price: 3.49,
        description: "Authentic South Indian madras filter coffee.",
      },
      {
        name: "Masala Chai",
        price: 3.49,
        description: "Indian tea brewed with aromatic spices and milk.",
      },
      {
        name: "Water Bottle",
        price: 1.99,
        description: "Chilled bottled water.",
      },
      {
        name: "Lemon Mint With Soda",
        price: 5.99,
        description: "Refreshing drink made with fresh lemon juice and mint leaves.",
      },
    ],
  },

  {
    title: "Buttermilk/Lassis",
    items: [
      {
        name: "Mango Lassi",
        price: 7.99,
        description: "Creamy yogurt drink blended with sweet mango pulp.",
      },
      {
        name: "Sweet Lassi",
        price: 5.99,
        description: "Sweet and creamy yogurt drink.",
      },
      {
        name: "Salted Lassi",
        price: 5.99,
        description: "Refreshing salted yogurt drink spiced with roasted cumin.",
      },
      {
        name: "Masala Chaas",
        price: 5.99,
        description: "Spiced buttermilk seasoned with coriander, ginger, and curry leaves.",
      },
    ],
  },

  {
    title: "Extra",
    items: [
      { name: "Curd", price: 0.99, description: "Fresh homemade yogurt." },
      { name: "Chutney", price: 0.99, description: "Coconut or savory chutney." },
      { name: "Masala", price: 0.99, description: "Extra portion of potato masala." },
      { name: "Sambar", price: 0.99, description: "Lentil and vegetable stew." },
      { name: "Ghee", price: 1.49, description: "Extra portion of ghee." },
      { name: "Cheese", price: 1.49, description: "Extra portion of cheese." },
      { name: "Paneer", price: 2.99, description: "Extra portion of paneer." },
      { name: "Puri", price: 2.49, description: "Fluffy deep-fried Indian bread." },
      { name: "Chapati", price: 2.49, description: "Whole wheat flatbread." },
      { name: "Idli", price: 2.49, description: "Steamed rice and lentil cake." },
      { name: "Vada", price: 2.49, description: "Crispy lentil fritter." },
      { name: "Plain Rice (8 oz)", price: 1.99, description: "Portion of steamed plain rice (8 oz)." },
      { name: "Plain Rice (16 oz)", price: 3.49, description: "Portion of steamed plain rice (16 oz)." },
      { name: "Plain Rice (24 oz)", price: 4.99, description: "Portion of steamed plain rice (24 oz)." },
      { name: "Papad", price: 0.49, description: "Fried, delicate, crispy cracker made with lentil flour." },
      { name: "White Chutney (8 oz)", price: 4.99, description: "Portion of fresh white chutney (8 oz)." },
      { name: "White Chutney (16 oz)", price: 8.99, description: "Portion of fresh white chutney (16 oz)." },
      { name: "White Chutney (24 oz)", price: 13.99, description: "Portion of fresh white chutney (24 oz)." },
      { name: "Red Chutney (8 oz)", price: 4.99, description: "Portion of fresh red chutney (8 oz)." },
      { name: "Red Chutney (16 oz)", price: 8.99, description: "Portion of fresh red chutney (16 oz)." },
      { name: "Red Chutney (24 oz)", price: 13.99, description: "Portion of fresh red chutney (24 oz)." },
      { name: "Green Chutney (8 oz)", price: 4.99, description: "Portion of fresh green chutney (8 oz)." },
      { name: "Green Chutney (16 oz)", price: 8.99, description: "Portion of fresh green chutney (16 oz)." },
      { name: "Green Chutney (24 oz)", price: 13.99, description: "Portion of fresh green chutney (24 oz)." },
      { name: "Sambar (8 oz)", price: 4.99, description: "Portion of Sambar (8 oz)." },
      { name: "Sambar (16 oz)", price: 8.99, description: "Portion of Sambar (16 oz)." },
      { name: "Sambar (24 oz)", price: 13.99, description: "Portion of Sambar (24 oz)." },
      {
        name: "Masala Chai Takeout",
        price: 3.99,
        description: "Indian tea brewed with aromatic spices and milk.",
      },
      {
        name: "Madras Filter Coffee Takeout",
        price: 3.99,
        description: "Authentic South Indian madras filter coffee.",
      },
    ],
  },

];

/* ================= PAGE ================= */
export default function Menu() {
  return (
    <div className="relative min-h-screen">
      <MobileNavbar />

      <div
        className="absolute inset-0"
        style={{
          backgroundColor: theme.bg,
          backgroundImage: `url(${templeOverlay})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          opacity: 0.18,
          mixBlendMode: "overlay",
          zIndex: 0,
        }}
      />

      <main className="relative z-10" style={{ padding: "48px 16px" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>

<header
  className="
    relative
    mb-7
    flex flex-col items-center gap-3
    md:block
    text-center
  "
>
  {/* MENU */}
  <h2
    style={{
      fontFamily: fonts.heading,
      color: theme.ink,
      fontWeight: 900,
      fontSize: 36,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      margin: 0,
    }}
  >
    Menu
  </h2>

  {/* ORDER NOW */}
  <Link
    to="https://carnaticcafe.ezybite.com/"
    className="
      w-full md:w-auto
      md:absolute md:right-0 md:top-1/2 md:-translate-y-1/2
      inline-flex items-center justify-center
      px-6 py-3
      text-lg md:text-2xl
      font-extrabold
      rounded-full
      shadow-lg
      transition-all duration-200
      hover:scale-105
    "
    style={{
    backgroundColor: "#F97316",   // solid Carnatic gold
    color: "#000000",
boxShadow: "0 10px 28px rgba(0,0,0,0.3)"
    }}
  >
  🛒  Order Now
  </Link>
</header>




          {menuData.map((sec, i) => (
            <Section key={i} {...sec} />
          ))}

          <footer
            style={{
              textAlign: "center",
              marginTop: 28,
              color: theme.ink,
              fontFamily: fonts.heading,
              fontSize: 36,
            }}
          >
            Please inform us of any allergies*.
          </footer>
        </div>
      </main>
    </div>
  );
}
